import styles from './list-item.module.css';

type ListItemNoteTone = 'danger' | 'muted' | 'accent';

type ListItemNote = {
  label: string;
  tone: ListItemNoteTone;
};

const NOTE_CLASS: Record<ListItemNoteTone, string | undefined> = {
  danger: styles.noteDanger,
  muted: styles.note,
  accent: styles.noteAccent,
};

type ListItemActionVariant = 'filled' | 'quiet';

type ListItemAction = {
  label: string;
  variant?: ListItemActionVariant;
  onSelect: () => void;
};

type ListItemProps = {
  title: string;
  isTitleWrapping?: boolean;
  marker?: string;
  subtitle?: string;
  note?: ListItemNote;
  isSecondLineWrapping?: boolean;
  value?: string;
  /** A quieter second line under the value, for the same fact said another way. */
  valueNote?: string;
  chevron?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  /** A button inside the row for the one thing the row is usually opened for. */
  action?: ListItemAction;
};

export const ListItem = ({
  title,
  isTitleWrapping = false,
  marker,
  subtitle,
  note,
  isSecondLineWrapping = false,
  value,
  valueNote,
  chevron = false,
  disabled = false,
  onClick,
  action,
}: ListItemProps) => {
  // With a button in the row the value moves under the title: three columns would leave the
  // title and the date a few characters each.
  const isValueInline = action !== undefined && value !== undefined;
  const hasSecondLine = subtitle !== undefined || note !== undefined || isValueInline;
  const wrapsSecondLine = isValueInline || isSecondLineWrapping;
  const content = (
    <>
      {marker !== undefined && (
        <span className={styles.marker} style={{ background: marker }} aria-hidden="true" />
      )}
      <span className={styles.text}>
        <span className={`${styles.title} ${isTitleWrapping ? styles.titleWrapping : ''}`}>
          {title}
        </span>
        {hasSecondLine && (
          <span className={`${styles.secondLine} ${wrapsSecondLine ? styles.wrapping : ''}`}>
            {subtitle !== undefined && <span className={styles.subtitle}>{subtitle}</span>}
            {note !== undefined && <span className={NOTE_CLASS[note.tone]}>{note.label}</span>}
            {isValueInline && <span className={styles.inlineValue}>{value}</span>}
          </span>
        )}
      </span>
      {value !== undefined && !isValueInline && (
        <span className={styles.valueColumn}>
          <span className={styles.value}>{value}</span>
          {valueNote !== undefined && <span className={styles.valueNote}>{valueNote}</span>}
        </span>
      )}
      {chevron && <span className={styles.chevron} aria-hidden="true" />}
    </>
  );

  if (onClick === undefined) {
    return (
      <li className={styles.item}>
        <div className={styles.row}>{content}</div>
      </li>
    );
  }

  // Two buttons side by side rather than one inside the other: a button may not contain a button.
  if (action !== undefined) {
    return (
      <li className={styles.item}>
        <div className={styles.split}>
          <button
            type="button"
            className={`${styles.row} ${styles.clickable} ${styles.main}`}
            disabled={disabled}
            onClick={onClick}
          >
            {content}
          </button>
          <button
            type="button"
            className={`${styles.action} ${action.variant === 'quiet' ? styles.actionQuiet : ''}`}
            disabled={disabled}
            onClick={action.onSelect}
          >
            {action.label}
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className={styles.item}>
      <button
        type="button"
        className={`${styles.row} ${styles.clickable}`}
        disabled={disabled}
        onClick={onClick}
      >
        {content}
      </button>
    </li>
  );
};
